// Only headings inside this component's explicit article-body region are indexed.
class SArticleToc extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    const body = this.querySelector('[data-article-body]');
    const panel = this.querySelector('[data-article-toc-panel]');
    const list = this.querySelector('[data-article-toc-list]');
    if (!body || !panel || !list) return;
    const headings = [...body.querySelectorAll('h2')];
    // Reserve existing IDs across the page; never overwrite an author's anchor.
    const ids = new Set([...document.querySelectorAll('[id]')].map(element => element.id));
    headings.forEach((heading, index) => {
      if (heading.id) return;
      const base = heading.textContent.trim().toLowerCase().normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '').replace(/[^\p{L}\p{N}]+/gu, '-')
        .replace(/^-+|-+$/g, '') || `section-${index + 1}`;
      let id = base;
      let suffix = 2;
      while (ids.has(id)) id = `${base}-${suffix++}`;
      heading.id = id;
      ids.add(id);
    });
    if (headings.length < 3) return;
    list.replaceChildren();
    const count = this.querySelector('[data-article-toc-count]');
    if (count) count.textContent = `· ${(count.dataset.countTemplate || '__COUNT__').replace('__COUNT__', headings.length)}`;
    headings.forEach((heading, index) => {
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${encodeURIComponent(heading.id)}`;
      const number = document.createElement('span');
      number.textContent = String(index + 1);
      number.setAttribute('aria-hidden', 'true');
      number.className = 'inline-flex size-6 shrink-0 items-center justify-center rounded-full border border-accent text-xs font-semibold text-accent';
      const label = document.createElement('span');
      label.textContent = heading.textContent.trim();
      link.append(number, label);
      link.className = 'flex min-h-11 items-center gap-3 py-1 text-on-surface hover:text-accent hover:underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent';
      link.addEventListener('click', event => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        history.pushState(null, '', link.hash);
        scrollToHeading(heading);
      }, { signal });
      item.append(link);
      list.append(item);
    });
    const desktop = matchMedia('(min-width: 1024px)');
    const setExpanded = () => { panel.open = desktop.matches; };
    setExpanded();
    desktop.addEventListener('change', setExpanded, { signal });
    panel.hidden = false;
    // Measure the real sticky header instead of assuming a fixed desktop height.
    const header = document.querySelector('[data-header-nav-group]')?.closest('header');
    const updateOffset = () => {
      const offset = (header?.getBoundingClientRect().height || 96) + 24;
      headings.forEach(heading => { heading.style.scrollMarginTop = `${offset}px`; });
      return offset;
    };
    const scrollToHeading = heading => {
      const offset = updateOffset();
      const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: heading.getBoundingClientRect().top + window.scrollY - offset, behavior: reduced ? 'instant' : 'smooth' });
    };
    updateOffset();
    this.headerObserver = new ResizeObserver(updateOffset);
    if (header) this.headerObserver.observe(header);
    // Generated anchors also work when arriving from a bookmarked section link.
    if (location.hash) {
      let id;
      try { id = decodeURIComponent(location.hash.slice(1)); } catch { return; }
      const target = headings.find(heading => heading.id === id);
      if (target) requestAnimationFrame(() => { if (this.isConnected) scrollToHeading(target); });
    }
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.headerObserver?.disconnect();
  }
}
if (!customElements.get('s-article-toc')) customElements.define('s-article-toc', SArticleToc);
